import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useQuery } from "@tanstack/react-query";
import { ReactNode, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../../assets/logo.png";
import logoLight from "../../assets/logo-pos.png";
import { environments, sinterHelpdeskUrl } from "../../environments";
import { useCheckPermissaoSegura } from "../../hooks/useCheckPermissaoSegura";
import { SinterAuditIntegracao, SinterAuditoriaStatus } from "../../interfaces";
import { ColorModeContext } from "../../providers/Theme";
import { getSinterAuditoria, getTenantConfig } from "../../services/api";
import { getMunicipio } from "../../services/data";
import { hasIntegracaoSinter } from "../../utils/integracaoSinter";
import { formatarDataBrasilia } from "../../utils/formatDate";
import {
  RESUMO_ERRO_NAO_IDENTIFICADO,
  formatSinterDetalhes,
  getSinterCib,
  getSinterDataHora,
  getSinterImobiliarioId,
  getSinterInscricaoCartografica,
  getSinterTipoRegistro,
  isSinterSucessoParcial,
} from "../../utils/sinterAuditoria";
import { DialogVersion } from "../Dashboard/DialogVersion";
import LoadingPage from "../LoadingPage";
import SinterDetalhesCell from "./SinterDetalhesCell";
import pj from "../../../package.json";

const SINTER_AUTO_REFRESH_MS = 60 * 60_000;
const SINTER_AUTO_REFRESH_LABEL = "1 hora";

const SINTER_ACTION_BAR_HEIGHT = 50;

const sinterRefreshQueryOptions = {
  refetchInterval: SINTER_AUTO_REFRESH_MS,
  refetchIntervalInBackground: false,
};

function formatHorarioUltimaAtualizacao(timestampMs: number): string {
  if (!timestampMs) return "—";
  return new Date(timestampMs).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function AutoRefreshStatus(props: {
  isFetching: boolean;
  ultimaAtualizacaoMs: number;
  intervalLabel: string;
  onRefresh: () => void;
  disabled?: boolean;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        height: SINTER_ACTION_BAR_HEIGHT,
        boxSizing: "border-box",
        pl: 1.5,
        pr: 0.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.5 : 0.85),
      }}
    >

      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
          bgcolor: props.isFetching ? theme.palette.warning.main : theme.palette.success.main,
          animation: props.isFetching
            ? "sinterDotPulseActive 1s ease-in-out infinite"
            : "sinterDotPulseGreen 2s ease-in-out infinite",
          "@keyframes sinterDotPulseGreen": {
            "0%, 100%": {
              opacity: 1,
              transform: "scale(1)",
              boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.45)}`,
            },
            "50%": {
              opacity: 0.75,
              transform: "scale(1.15)",
              boxShadow: `0 0 0 7px ${alpha(theme.palette.success.main, 0)}`,
            },
          },
          "@keyframes sinterDotPulseActive": {
            "0%, 100%": {
              opacity: 1,
              transform: "scale(1)",
              boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.45)}`,
            },
            "50%": {
              opacity: 0.7,
              transform: "scale(1.15)",
              boxShadow: `0 0 0 7px ${alpha(theme.palette.warning.main, 0)}`,
            },
          },
        }}
      />
      <Tooltip
        title={`Atualização automática a cada ${props.intervalLabel}`}
        arrow
        placement="bottom"
      >

        <Box sx={{ minWidth: 0, flex: 1, py: 0.25 }}>
          <Typography variant="caption" fontWeight={600} lineHeight={1.25} noWrap display="block">
            Atualização automática
          </Typography>
          <Typography variant="caption" color="text.secondary" lineHeight={1.25} noWrap display="block">
            A cada {props.intervalLabel} · {formatHorarioUltimaAtualizacao(props.ultimaAtualizacaoMs)}
          </Typography>
        </Box>
      </Tooltip>

      <Box
        sx={{
          width: "1px",
          alignSelf: "stretch",
          my: 1,
          bgcolor: alpha(theme.palette.divider, 0.2),
          flexShrink: 0,
        }}
      />
      <Tooltip title="Atualizar agora" arrow placement="bottom">
        <span>
          <IconButton
            size="small"
            onClick={props.onRefresh}
            disabled={props.disabled || props.isFetching}

            sx={{ flexShrink: 0 }}
          >
            {props.isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function StatCard(props: {
  label: string;
  value?: number | null;
  icon: ReactNode;
  accent?: "default" | "success" | "error";
  isEstimate?: boolean;
}) {
  const theme = useTheme();

  const color =
    props.accent === "success"
      ? theme.palette.success.main
      : props.accent === "error"
        ? theme.palette.error.main
        : theme.palette.text.primary;

  const iconColor =
    props.accent === "success"
      ? theme.palette.success.main
      : props.accent === "error"
        ? theme.palette.error.main
        : theme.palette.text.secondary;

  const formattedValue =
    props.value === undefined || props.value === null
      ? "—"
      : `${props.isEstimate ? "≈ " : ""}${props.value.toLocaleString("pt-BR")}`;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 140,
        px: 2.5,
        py: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.4 : 0.8),
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.3 }}>
            {props.label}
          </Typography>
          <Typography variant="h4" fontWeight={600} sx={{ mt: 0.5, color, lineHeight: 1.2 }}>
            {formattedValue}
          </Typography>
        </Box>
        <Box sx={{ color: iconColor, opacity: 0.85, mt: 0.25 }}>{props.icon}</Box>
      </Box>
    </Box>
  );
}

function getFirstAuditValue(row: SinterAuditIntegracao, keys: string[]) {
  const record = row as SinterAuditIntegracao & Record<string, unknown>;
  return keys.find((key) => {
    const value = record[key];
    return value !== undefined && value !== null && value !== "";
  });
}

function getAuditFieldValue(row: SinterAuditIntegracao, keys: string[]) {
  const record = row as SinterAuditIntegracao & Record<string, unknown>;
  const key = getFirstAuditValue(row, keys);
  return key ? record[key] : null;
}

function formatAuditJsonValue(value: unknown) {
  if (value === undefined || value === null || value === "") return "Sem informação.";

  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function AuditJsonBlock(props: { title: string; value: unknown }) {
  const theme = useTheme();

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {props.title}
      </Typography>

      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          maxHeight: 320,
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
          bgcolor: alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.55 : 0.75),
          color: "text.primary",
          fontFamily: "monospace",
          fontSize: "0.8125rem",
          lineHeight: 1.6,
        }}
      >
        {formatAuditJsonValue(props.value)}
      </Box>
    </Box>
  );
}

export default function SinterAuditoriaPage() {
  const autorizado = useCheckPermissaoSegura();
  const theme = useTheme();
  const color = useContext(ColorModeContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const municipio = getMunicipio();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<SinterAuditoriaStatus>("todos");
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAuditRecord, setSelectedAuditRecord] = useState<SinterAuditIntegracao | null>(null);
  const [payloadAuditRecord, setPayloadAuditRecord] = useState<SinterAuditIntegracao | null>(null);

  useEffect(() => {
    if (search.trim() === appliedSearch) return;
    const timer = window.setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(0);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search, appliedSearch]);

  useEffect(() => {
    if (!searchParams.has("municipio")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("municipio");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: integracaoDisponivel, isLoading: loadingIntegracao } = useQuery({
    queryKey: ["sinter-integracao-disponivel", municipio],
    queryFn: async () => {
      const config = await getTenantConfig();
      return hasIntegracaoSinter(config);
    },
  });

  const {
    data: auditoria,
    isLoading: loadingTable,
    isError: errorTable,
    refetch: refetchTable,
    isFetching: isFetchingTable,
    dataUpdatedAt: tableUpdatedAt,
  } = useQuery({
    queryKey: ["sinter-auditoria", municipio, page, rowsPerPage, statusFilter, appliedSearch],
    queryFn: ({ signal }) => getSinterAuditoria(page + 1, rowsPerPage, statusFilter, appliedSearch, signal),
    enabled: integracaoDisponivel === true,
    ...sinterRefreshQueryOptions,
  });

  const isFetching = isFetchingTable;
  const ultimaAtualizacaoMs = tableUpdatedAt;

  const rows = auditoria?.results ?? [];

  const surface = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.35 : 0.9),
    overflow: "hidden",
  };

  function handleVoltarDashboard() {
    const loaded = localStorage.getItem("loaded");
    const idLote = loaded ? JSON.parse(loaded) : null;
    if (idLote) {
      navigate(`/dashboard/${idLote}`);
      return;
    }
    navigate("/");
  }

  async function handleAtualizar() {
    const result = await refetchTable();
    if (!result.isError) toast.success("Lista atualizada.");
  }

  function buildHelpdeskUrl(record: SinterAuditIntegracao) {
    const url = new URL(sinterHelpdeskUrl);
    const dataHora = getSinterDataHora(record);
    const inscricao = getSinterInscricaoCartografica(record);

    url.searchParams.set("origem", "sinter-auditoria");
    url.searchParams.set("id_auditoria", String(record.id_audit_integracao));
    url.searchParams.set("municipio", municipio);
    if (inscricao) url.searchParams.set("inscricao_cartografica", inscricao);
    if (record.endpoint_url || record.endpoint) url.searchParams.set("endpoint", String(record.endpoint_url || record.endpoint));
    if (dataHora) url.searchParams.set("data_hora", dataHora);
    return url.toString();
  }

  function handleReportar(record: SinterAuditIntegracao) {
    window.open(buildHelpdeskUrl(record), "_blank", "noopener,noreferrer");
  }

  if (!autorizado) {
    return <LoadingPage message="Verificando permissões de acesso" />;
  }

  if (loadingIntegracao) {
    return <LoadingPage message="Verificando integração SINTER" />;
  }

  if (integracaoDisponivel === false) {

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: theme.palette.mode === "dark" ? "#0d0f12" : "#f4f6f8",
          px: 2,
        }}
      >

        <Box
          sx={{
            maxWidth: 420,
            width: "100%",
            p: 3,
            borderRadius: 3,
            textAlign: "center",
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.4 : 0.95),
          }}
        >

          <BlockOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Auditoria SINTER indisponível
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            A integração SINTER não está habilitada para o município atual.
          </Typography>

          <Button variant="contained" onClick={handleVoltarDashboard} sx={{ borderRadius: 2, textTransform: "none" }}>
            Voltar ao dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  const filters: { value: SinterAuditoriaStatus; label: string; icon: ReactNode }[] = [
    { value: "todos", label: "Todos", icon: <FilterListIcon fontSize="small" /> },
    { value: "sucesso", label: "Sucesso", icon: <CheckCircleOutlineIcon fontSize="small" color="success" /> },
    { value: "erro", label: "Erro", icon: <ErrorOutlineIcon fontSize="small" color="error" /> },
  ];

  const filterAtivo = filters.find((f) => f.value === statusFilter);
  const filterMenuAberto = Boolean(filterAnchorEl);
  const selectedPayloadEnvio = payloadAuditRecord?.json_reversa ?? null;
  const selectedRetorno = payloadAuditRecord
    ? getAuditFieldValue(payloadAuditRecord, [
        "response",
        "response_body",
        "responseBody",
        "resposta",
        "payload_retorno",
        "payloadRetorno",
        "response_payload",
        "responsePayload",
        "retorno_api",
        "retornoApi",
        "mensagem_retorno",
        "mensagemRetorno",
        "resultado",
        "body_retorno",
        "bodyRetorno",
        "body_resposta",
        "bodyResposta",
        "error",
        "erro",
        "mensagem_error",
        "mensagemError",
        "mensagem_erro",
        "mensagemErro",
        "mensagem",
      ])
    : null;
  const selectedErroBruto = payloadAuditRecord
    ? getAuditFieldValue(payloadAuditRecord, [
        "mensagem_erro",
        "node_workflow_error",
        "error",
        "erro",
        "mensagemError",
        "mensagem_erro_bruto",
      ])
    : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.mode === "dark" ? "#0d0f12" : "#f4f6f8",
        pb: 6,
      }}
    >

      <Box
        sx={{
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          backdropFilter: "blur(8px)",
        }}
      >

        <Box
          sx={{
            maxWidth: 1100,
            mx: "auto",
            px: { xs: 2, sm: 3 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <img
              className="logo-dashboard"
              src={theme.palette.mode === "dark" ? logo : logoLight}
              alt="logo"

              style={{ height: 32 }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                Caracterização
              </Typography>
              <DialogVersion>
                <Typography variant="caption" color="text.secondary">
                  {pj.version} · {environments.ambiente}
                </Typography>
              </DialogVersion>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Voltar">
              <IconButton size="small" onClick={handleVoltarDashboard}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={color.toggleColorMode}>
              {theme.palette.mode === "dark" ? <Brightness7Icon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, sm: 3 }, pt: 4 }}>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "flex-end" }}
          gap={2}
          sx={{ mb: 3 }}
        >
          <Box>

            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
              Integração
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 0.25 }}>
              Auditoria SINTER
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <AutoRefreshStatus
              isFetching={isFetching}
              ultimaAtualizacaoMs={ultimaAtualizacaoMs}
              intervalLabel={SINTER_AUTO_REFRESH_LABEL}
              onRefresh={handleAtualizar}
              disabled={loadingTable}
            />
          </Stack>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} sx={{ mb: 3 }}>
          <StatCard
            label="Total"
            value={auditoria?.stats?.total ?? auditoria?.total_records}
            isEstimate={auditoria?.stats?.is_estimate ?? auditoria?.total_records_is_estimate}
            icon={<StorageOutlinedIcon />}
          />
          <StatCard
            label="Sucesso"
            value={auditoria?.stats?.sucesso}
            isEstimate={auditoria?.stats?.is_estimate}
            accent="success"
            icon={<CheckCircleOutlineIcon />}
          />
          <StatCard
            label="Erro"
            value={auditoria?.stats?.erro}
            isEstimate={auditoria?.stats?.is_estimate}
            accent="error"
            icon={<ErrorOutlineIcon />}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por inscrição, imóvel, CIB, tipo ou detalhes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}

            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title={`Filtro: ${filterAtivo?.label ?? "Todos"}`}>
            <IconButton
              size="small"
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}

              sx={{
                flexShrink: 0,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                color: statusFilter !== "todos" ? theme.palette.primary.main : "inherit",
              }}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={filterAnchorEl}
            open={filterMenuAberto}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}

            PaperProps={{
              sx: { minWidth: 160, borderRadius: 2, mt: 0.5 },
            }}
          >
            {filters.map((f) => (
              <MenuItem
                key={f.value}
                selected={statusFilter === f.value}
                onClick={() => {
                  setStatusFilter(f.value);
                  setPage(0);
                  setFilterAnchorEl(null);
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>{f.icon}</ListItemIcon>
                <ListItemText primary={f.label} />
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        <Box sx={surface}>

          {errorTable ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Não foi possível consultar a auditoria SINTER. Tente atualizar a lista.
            </Alert>
          ) : loadingTable ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {["Data/Hora", "Id do Imóvel", "Inscrição Cartográfica", "CIB", "Tipo", "Status", "Detalhes", ""].map((col) => (
                        <TableCell
                          key={col || "actions"}
                          align={col === "" ? "right" : "left"}

                          sx={{
                            py: 1.5,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                            color: "text.secondary",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            letterSpacing: 0.2,
                          }}
                        >
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>

                        <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                          <Typography variant="body2" color="text.secondary">
                            Nenhum registro encontrado.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => {
                        const dataHora = getSinterDataHora(row);
                        const imobiliarioId = getSinterImobiliarioId(row);
                        const inscricaoCartografica = getSinterInscricaoCartografica(row);
                        const cib = getSinterCib(row);
                        const detalhes = formatSinterDetalhes(row);
                        const sucessoParcial = isSinterSucessoParcial(row);
                        const isErroNaoIdentificado = !row.processado && detalhes.resumo === RESUMO_ERRO_NAO_IDENTIFICADO;

                        return (
                          <TableRow
                            key={row.id_audit_integracao}
                            hover
                            onClick={() => setSelectedAuditRecord(row)}

                            sx={{
                              cursor: "pointer",
                              bgcolor: selectedAuditRecord?.id_audit_integracao === row.id_audit_integracao ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.06) : undefined,
                              "&:last-child td": { border: 0 },
                              "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.04) },
                            }}
                          >

                            <TableCell sx={{ py: 2, whiteSpace: "nowrap", fontSize: "0.875rem" }}>
                              {dataHora ? formatarDataBrasilia(dataHora) : "—"}
                            </TableCell>

                            <TableCell sx={{ py: 2, fontFamily: "monospace", fontSize: "0.875rem" }}>
                              {imobiliarioId ?? "—"}
                            </TableCell>

                            <TableCell sx={{ py: 2, fontFamily: "monospace", fontSize: "0.875rem" }}>
                              {inscricaoCartografica ?? "-"}
                            </TableCell>

                            <TableCell sx={{ py: 2, fontFamily: "monospace", fontSize: "0.875rem" }}>
                              {cib ?? "—"}
                            </TableCell>

                            <TableCell sx={{ py: 2, fontSize: "0.875rem" }}>{getSinterTipoRegistro(row)}</TableCell>

                            <TableCell sx={{ py: 2 }}>
                              <Chip
                                size="small"
                                label={sucessoParcial ? "Sucesso parcial" : row.processado ? "Sucesso" : "Erro"}
                                icon={row.processado ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                                color={row.processado ? "success" : "error"}
                                variant={row.processado ? "outlined" : "filled"}

                                sx={{
                                  fontWeight: 500,
                                  borderRadius: 1.5,
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ py: 2, verticalAlign: "top" }}>
                              <SinterDetalhesCell detalhes={detalhes} parcial={sucessoParcial} />
                            </TableCell>

                            <TableCell align="right" sx={{ py: 2 }}>
                              <Stack direction="row" gap={0.5} justifyContent="flex-end">
                                {isErroNaoIdentificado && (
                                  <Tooltip title="Reportar">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleReportar(row);
                                        }}
                                        sx={{ color: "warning.main" }}
                                      >
                                        <ReportProblemOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                <Tooltip title="Visualizar Payload">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setPayloadAuditRecord(row);
                                      }}
                                      sx={{ color: "text.secondary" }}
                                    >
                                      <VisibilityOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={auditoria?.total_records ?? 0}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Por página"

                sx={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
              />
            </>
          )}
        </Box>
      </Box>

      <Dialog
        open={Boolean(payloadAuditRecord)}
        onClose={() => setPayloadAuditRecord(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{

          sx: {
            borderRadius: 3,
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.95 : 1),
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="h6" fontWeight={600}>
              Visualizar Payload
            </Typography>
            <Chip
              label={`ID #${payloadAuditRecord?.id_audit_integracao ?? "—"}`}
              color="primary"
              size="small"
              variant="outlined"
              sx={{ flexShrink: 0, fontWeight: 600 }}
            />
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack gap={2.5}>
            <AuditJsonBlock title="Payload enviado" value={selectedPayloadEnvio} />
            <AuditJsonBlock title="Retorno da API" value={selectedRetorno} />
            <AuditJsonBlock title="Erro bruto" value={selectedErroBruto} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {payloadAuditRecord && (
            <Button
              onClick={() => handleReportar(payloadAuditRecord)}
              startIcon={<ReportProblemOutlinedIcon />}
              sx={{ borderRadius: 2, textTransform: "none", mr: "auto" }}
            >
              Reportar
            </Button>
          )}
          <Button onClick={() => setPayloadAuditRecord(null)} sx={{ borderRadius: 2, textTransform: "none" }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
